import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './OnboardWizard.css';

// Import step components
import ProviderSelectionStep from './add-pipe/ProviderSelectionStep';
import BasicInfoStep from './add-pipe/BasicInfoStep';
import RoutingStep from './add-pipe/RoutingStep';
import ConnectionTestStep from './add-pipe/ConnectionTestStep';
import NetworkingStep from './add-pipe/NetworkingStep';
import CostStep from './add-pipe/CostStep';
import OverridePolicyStep from './add-pipe/OverridePolicyStep';
import LLMPipeConfirmStep from './add-pipe/LLMPipeConfirmStep';
import CompletePipeStep from './add-pipe/CompletePipeStep';

// Define wizard steps
type WizardStep = 'provider' | 'basic-info' | 'routing' | 'test' | 'networking' | 'cost' | 'override-policy' | 'confirm' | 'creating' | 'complete';

interface PipeConfig {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  listen_address?: string;
  route?: string;
  pipe_type: {
    type: string;
    provider: any; // LLMProvider tagged enum
  };
  rate_limit?: {
    enabled?: boolean;
    requests_per_minute?: number;
    tokens_per_minute?: number;
  };
  cost_tracking?: {
    enabled: boolean;
    input_price_per_1k?: number;
    output_price_per_1k?: number;
    monthly_budget?: number;
  };
  override_policy?: Record<string, string>;
  created_at: string;
  updated_at?: string;
}

interface WizardState {
  currentStep: WizardStep;
  provider: string | null;
  name: string;
  description: string;
  model: string;
  endpoint: string;
  secretId: string;
  azureApiVersion: string;
  region: string;
  modelParameters: Record<string, any>; // Dynamic parameters based on selected model
  // Listener routing fields
  selectedHostPort: string;
  selectedPrefix: string;
  customPath: string;
  listen_address: string;
  route: string;
  requestsPerMinute: number;
  tokensPerMinute: number;
  enableCostTracking: boolean;
  inputCostPer1K: number;
  outputCostPer1K: number;
  monthlyBudget: number;
  overridePolicy: Record<string, string> | null;
  pipeConfig: PipeConfig | null;
}

const AddPipeWizardPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 'provider',
    provider: null,
    name: '',
    description: '',
    model: '',
    endpoint: '',
    secretId: '',
    azureApiVersion: '',
    region: '',
    modelParameters: {},
    selectedHostPort: '',
    selectedPrefix: '',
    customPath: '',
    listen_address: '',
    route: '',
    requestsPerMinute: 60,
    tokensPerMinute: 90000,
    enableCostTracking: true,
    inputCostPer1K: 0,
    outputCostPer1K: 0,
    monthlyBudget: 0,
    overridePolicy: null,
    pipeConfig: null,
  });

  const [error, setError] = useState<string | null>(null);

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'provider', label: 'Provider' },
    { key: 'basic-info', label: 'Configure' },
    { key: 'test', label: 'Test' },
    { key: 'routing', label: 'Routing' },
    { key: 'networking', label: 'Networking' },
    { key: 'cost', label: 'Cost' },
    { key: 'override-policy', label: 'Overrides' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'creating', label: 'Creating' },
    { key: 'complete', label: 'Complete' },
  ];
  const currentStepIndex = steps.findIndex(s => s.key === wizardState.currentStep);

  const goToStep = (step: WizardStep) => {
    setWizardState(prev => ({ ...prev, currentStep: step }));
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      goToStep(steps[nextIndex].key);
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(steps[prevIndex].key);
    }
  };

  const handleProviderSelect = (provider: string) => {
    setWizardState(prev => ({ ...prev, provider }));
    nextStep();
  };

  const handleBasicInfoChange = (data: {
    name: string;
    description: string;
    model: string;
    endpoint: string;
    secretId: string;
    modelParameters?: Record<string, any>;
    azureApiVersion?: string;
    region?: string;
  }) => {
    setWizardState(prev => ({
      ...prev,
      name: data.name,
      description: data.description,
      model: data.model,
      endpoint: data.endpoint,
      secretId: data.secretId,
      modelParameters: data.modelParameters || {},
      azureApiVersion: data.azureApiVersion || '',
      region: data.region || '',
    }));
  };

  const handleRoutingChange = (data: {
    selectedHostPort: string;
    selectedPrefix: string;
    customPath: string;
    listen_address: string;
    route: string;
    name: string;
    description: string;
  }) => {
    setWizardState(prev => ({
      ...prev,
      selectedHostPort: data.selectedHostPort,
      selectedPrefix: data.selectedPrefix,
      customPath: data.customPath,
      listen_address: data.listen_address,
      route: data.route,
      name: data.name,
      description: data.description,
    }));
  };

  const handleNetworkingChange = (data: {
    requestsPerMinute: number;
    tokensPerMinute: number;
  }) => {
    setWizardState(prev => ({
      ...prev,
      requestsPerMinute: data.requestsPerMinute,
      tokensPerMinute: data.tokensPerMinute,
    }));
  };

  const handleCostChange = (data: {
    enableCostTracking: boolean;
    inputCostPer1K: number;
    outputCostPer1K: number;
    monthlyBudget: number;
  }) => {
    setWizardState(prev => ({
      ...prev,
      enableCostTracking: data.enableCostTracking,
      inputCostPer1K: data.inputCostPer1K,
      outputCostPer1K: data.outputCostPer1K,
      monthlyBudget: data.monthlyBudget,
    }));
  };

  const handleOverridePolicyChange = (data: {
    overridePolicy: Record<string, string> | null;
  }) => {
    setWizardState(prev => ({
      ...prev,
      overridePolicy: data.overridePolicy,
    }));
  };

  const handleConfirm = async () => {
    setError(null);
    goToStep('creating');

    try {
      // Generate a UUID for the pipe ID
      const pipeId = crypto.randomUUID();
      
      // Build provider config based on type
      let providerConfig: any;
      
      switch (wizardState.provider) {
        case 'openai':
          providerConfig = {
            provider_type: 'openai',
            model: wizardState.model,
            api_key_secret: wizardState.secretId,
            base_url: wizardState.endpoint !== 'https://api.openai.com/v1/chat/completions' 
              ? wizardState.endpoint 
              : undefined,
            ...wizardState.modelParameters,
          };
          break;
        case 'anthropic':
          providerConfig = {
            provider_type: 'anthropic',
            model: wizardState.model,
            api_key_secret: wizardState.secretId,
            ...wizardState.modelParameters,
          };
          break;
        case 'gemini':
          providerConfig = {
            provider_type: 'gemini',
            model: wizardState.model,
            api_key_secret: wizardState.secretId,
            ...wizardState.modelParameters,
          };
          break;
        case 'bedrock':
          providerConfig = {
            provider_type: 'bedrock',
            model: wizardState.model,
            region: wizardState.region || 'us-east-1',
            access_key_secret: wizardState.secretId,
            ...wizardState.modelParameters,
          };
          break;
        case 'azure_openai':
          providerConfig = {
            provider_type: 'azure_openai',
            deployment_name: wizardState.model,
            endpoint: wizardState.endpoint,
            api_key_secret: wizardState.secretId,
            api_version: wizardState.azureApiVersion || '2024-02-15-preview',
            ...wizardState.modelParameters,
          };
          break;
        default:
          providerConfig = {
            provider_type: 'custom',
            name: wizardState.model,
            endpoint: wizardState.endpoint,
            auth_secret: wizardState.secretId,
            ...wizardState.modelParameters,
          };
      }

      // Build pipe configuration
      const config: PipeConfig = {
        id: pipeId,
        name: wizardState.name,
        description: wizardState.description,
        enabled: true,
        listen_address: wizardState.listen_address || undefined,
        route: wizardState.route || undefined,
        pipe_type: {
          type: 'llm',
          provider: providerConfig,
        },
        rate_limit: wizardState.requestsPerMinute > 0 ? {
          enabled: true,
          requests_per_minute: wizardState.requestsPerMinute,
          tokens_per_minute: wizardState.tokensPerMinute || undefined,
        } : {
          enabled: false,
          requests_per_minute: 60, // Provide default when disabled
          tokens_per_minute: undefined,
        },
        cost_tracking: {
          enabled: wizardState.enableCostTracking,
          input_price_per_1k: wizardState.inputCostPer1K || undefined,
          output_price_per_1k: wizardState.outputCostPer1K || undefined,
          monthly_budget: wizardState.monthlyBudget || undefined,
        },
        override_policy: wizardState.overridePolicy || undefined,
        created_at: new Date().toISOString(),
      };

      // Call API to create pipe
      const response = await fetch('/api/v1/pipes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create pipe';
        try {
          const errorText = await response.text();
          console.error('API Error Response:', errorText);
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch {
            // If not valid JSON, use the text as-is
            errorMessage = errorText || errorMessage;
          }
        } catch {
          // Could not read response
          errorMessage = `Failed to create pipe (${response.status} ${response.statusText})`;
        }
        console.error('Failed to create pipe:', errorMessage);
        throw new Error(errorMessage);
      }

      const createdPipe = await response.json();
      
      setWizardState(prev => ({ ...prev, pipeConfig: createdPipe }));
      goToStep('complete');
    } catch (err: any) {
      console.error('Error in handleConfirm:', err);
      setError(err.message || 'Failed to create pipe');
      // Go back to confirm step to show error
      goToStep('confirm');
    }
  };

  const handleFinish = () => {
    navigate('/pipes');
  };

  const handleCancel = () => {
    navigate('/pipes');
  };

  // Render current step
  const renderStep = () => {
    switch (wizardState.currentStep) {
      case 'provider':
        return (
          <ProviderSelectionStep
            onProviderSelect={handleProviderSelect}
            onCancel={handleCancel}
          />
        );
      case 'basic-info':
        return (
          <BasicInfoStep
            provider={wizardState.provider!}
            initialName={wizardState.name}
            initialDescription={wizardState.description}
            initialModel={wizardState.model}
            initialEndpoint={wizardState.endpoint}
            initialSecretId={wizardState.secretId}
            initialAzureApiVersion={wizardState.azureApiVersion}
            initialRegion={wizardState.region}
            onChange={handleBasicInfoChange}
            onNext={nextStep}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'routing':
        return (
          <RoutingStep
            provider={wizardState.provider!}
            initialSelectedHostPort={wizardState.selectedHostPort}
            initialSelectedPrefix={wizardState.selectedPrefix}
            initialCustomPath={wizardState.customPath}
            initialName={wizardState.name}
            initialDescription={wizardState.description}
            onChange={handleRoutingChange}
            onNext={nextStep}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'test':
        return (
          <ConnectionTestStep
            provider={wizardState.provider!}
            model={wizardState.model}
            endpoint={wizardState.endpoint}
            secretId={wizardState.secretId}
            azureApiVersion={wizardState.azureApiVersion}
            region={wizardState.region}
            onNext={nextStep}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'networking':
        return (
          <NetworkingStep
            provider={wizardState.provider!}
            model={wizardState.model}
            initialRequestsPerMinute={wizardState.requestsPerMinute}
            initialTokensPerMinute={wizardState.tokensPerMinute}
            onChange={handleNetworkingChange}
            onNext={nextStep}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'cost':
        return (
          <CostStep
            provider={wizardState.provider!}
            model={wizardState.model}
            initialEnableCostTracking={wizardState.enableCostTracking}
            initialInputCostPer1K={wizardState.inputCostPer1K}
            initialOutputCostPer1K={wizardState.outputCostPer1K}
            initialMonthlyBudget={wizardState.monthlyBudget}
            onChange={handleCostChange}
            onNext={nextStep}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'override-policy':
        return (
          <OverridePolicyStep
            provider={wizardState.provider!}
            model={wizardState.model}
            initialOverridePolicy={wizardState.overridePolicy || {}}
            onChange={handleOverridePolicyChange}
            onNext={nextStep}
            onBack={prevStep}
            onCancel={handleCancel}
          />
        );
      case 'confirm':
        return (
          <>
            {error && (
              <div className="alert alert-danger mb-4">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>Error:</strong> {error}
              </div>
            )}
            <LLMPipeConfirmStep
              provider={wizardState.provider!}
              name={wizardState.name}
              description={wizardState.description}
              model={wizardState.model}
              endpoint={wizardState.endpoint}
              secretId={wizardState.secretId}
              azureApiVersion={wizardState.azureApiVersion}
              region={wizardState.region}
              requestsPerMinute={wizardState.requestsPerMinute}
              tokensPerMinute={wizardState.tokensPerMinute}
              enableCostTracking={wizardState.enableCostTracking}
              inputCostPer1K={wizardState.inputCostPer1K}
              outputCostPer1K={wizardState.outputCostPer1K}
              monthlyBudget={wizardState.monthlyBudget}
              onBack={prevStep}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
            />
          </>
        );
      case 'creating':
        return (
          <div className="card shadow">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary mb-4" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden"></span>
              </div>
              <h4 className="mb-3">Creating LLM Pipe...</h4>
              <p className="text-muted">
                Please wait while we configure your LLM pipe connection.
              </p>
            </div>
          </div>
        );
      case 'complete':
        return (
          <CompletePipeStep
            pipeConfig={wizardState.pipeConfig!}
            onFinish={handleFinish}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={handleCancel}
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-plus-circle mr-2"></i>
          Add Pipe Wizard
        </h1>
        <div style={{ width: '80px' }}></div>
      </div>

      {/* Progress Bar */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="wizard-progress">
            <div className="wizard-steps">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className={`wizard-step ${
                    index === currentStepIndex
                      ? 'active'
                      : index < currentStepIndex
                      ? 'completed'
                      : ''
                  }`}
                >
                  <div className="wizard-step-circle">
                    {index < currentStepIndex ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="wizard-step-label">{step.label}</div>
                  {index < steps.length - 1 && <div className="wizard-step-line"></div>}
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStep()}
      </div><br/>
    </div>
  );
};

export default AddPipeWizardPage;
